import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import {
  Badge,
  Button,
  Field,
  Input,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Radio,
  RadioGroup,
  Spinner,
  Text,
} from '@fluentui/react-components';
import { Edit16Regular, Add16Regular, CheckmarkCircle20Regular, DismissCircle20Regular, Circle20Regular, Clock20Regular, LockClosed16Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { NewContractNegotiationVersionConcept } from '../../../models/Entities/Contracts/NewContractNegotiationVersionConcept';
import {
  DEFAULT_CONCEPT_STATUS,
  getStatusColor,
  getStatusLabel,
  mapDispatchToProps,
  mapStateToProps,
  OwnProps,
  Props,
  STATUS_OPTIONS,
} from './VersionConceptCell.Functions';
import { formatCurrency } from './NegotiationConceptGrid.Functions';
import { useNegotiationConceptGridStyles } from './NegotiationConceptGrid.Styles';

const StatusIcon = ({ status }: { status: number | undefined }) => {
  const color = getStatusColor(status);
  const label = getStatusLabel(status);
  const iconStyle = { color, flexShrink: 0 };
  switch (status) {
    case 2: return <CheckmarkCircle20Regular style={iconStyle} title={label} />;
    case 3: return <DismissCircle20Regular style={iconStyle} title={label} />;
    case 4: return <Clock20Regular style={iconStyle} title={label} />;
    default: return <Circle20Regular style={iconStyle} title={label} />; // offered=1 or unknown
  }
};

const VersionConceptCellComponent = (props: Props) => {
  const { t } = useTranslation();
  const styles = useNegotiationConceptGridStyles();

  const [isOpen, setIsOpen] = useState(false);
  const [sponsorCost, setSponsorCost] = useState<number>(0);
  const [siteCost, setSiteCost] = useState<number>(0);
  const [status, setStatus] = useState<number>(DEFAULT_CONCEPT_STATUS);

  // Reset local form state each time the popover opens
  useEffect(() => {
    if (isOpen && props.versionConcept) {
      setSponsorCost(props.versionConcept.sponsorCost ?? 0);
      setSiteCost(props.versionConcept.siteCost ?? 0);
      setStatus(props.versionConcept.negotiationConceptStatus ?? DEFAULT_CONCEPT_STATUS);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Auto-close once the update completes successfully
  const wasUpdatingRef = useRef(false);
  useEffect(() => {
    if (wasUpdatingRef.current && !props.isUpdating) {
      setIsOpen(false);
    }
    wasUpdatingRef.current = props.isUpdating;
  }, [props.isUpdating]);

  const handleSave = () => {
    const vc = props.versionConcept;
    if (vc) {
      props.UpdateContractNegotiationVersionConcept(
        {
          ...vc,
          currencyCode: props.currencyCode || vc.currencyCode || '',
          sponsorCost,
          siteCost,
          negotiationConceptStatus: status,
        },
        props.contractId,
        props.contractNegotiationId
      );
      // auto-close handled by wasUpdatingRef effect
    } else {
      const newEntity: NewContractNegotiationVersionConcept = {
        contractNegotiationVersionId: props.versionId,
        contractNegotiationConceptId: props.negotiationConceptId,
        currencyCode: props.currencyCode || '',
        sponsorCost,
        siteCost,
        sponsorReferenceVariance: 0,
        siteReferenceVariance: 0,
        negotiationConceptStatus: status,
      };
      props.CreateContractNegotiationVersionConcept(
        newEntity,
        props.contractId,
        props.contractNegotiationId
      );
      setIsOpen(false);
    }
  };

  const vc = props.versionConcept;

  // When this concept was already accepted/rejected in a prior version, show read-only locked cell
  if (props.isConceptFinalized) {
    return (
      <>
        <td
          colSpan={2}
          style={{
            padding: '4px 8px',
            opacity: 0.4,
            textAlign: 'center',
            whiteSpace: 'nowrap',
          }}
          title={t('versionConceptCell.finalizedTooltip', 'Concepto ya finalizado en una versión anterior')}
        >
          <LockClosed16Regular style={{ verticalAlign: 'middle', marginRight: 4 }} />
          {vc ? (
            <span>{formatCurrency(vc.siteCost)}</span>
          ) : (
            <span>-</span>
          )}
        </td>
      </>
    );
  }

  if (!vc) {
    return (
      <>
        <td className={styles.tdVersion} colSpan={2} style={{ padding: 0 }}>
          <Popover
            withArrow
            open={isOpen}
            onOpenChange={(_, data) => setIsOpen(!!data.open)}
          >
            <PopoverTrigger disableButtonEnhancement>
              <button
                className={styles.cellButton}
                title={t('versionConceptCell.addTooltip', 'Agregar valores')}
              >
                <span className={styles.emptyCell}>-</span>
                <Add16Regular style={{ opacity: 0.5, fontSize: 12, flexShrink: 0 }} />
              </button>
            </PopoverTrigger>
            <PopoverSurface
              tabIndex={-1}
              aria-label={t('versionConceptCell.addLabel', 'Agregar concepto de versión')}
            >
              <div
                style={{
                  minWidth: 270,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
              >
                <Text weight="semibold" size={300}>
                  {t('versionConceptCell.addTitle', 'Agregar valores')}
                </Text>

                <Field label={t('versionConceptCell.sponsorCost', 'Oferta sponsor')}>
                  <Input
                    type="number"
                    step={0.01}
                    value={String(sponsorCost)}
                    contentAfter={props.currencyCode ? <span>{props.currencyCode}</span> : undefined}
                    onChange={(_, data) => setSponsorCost(Number.parseFloat(data.value) || 0)}
                  />
                </Field>

                <Field label={t('versionConceptCell.siteCost', 'Contraoferta')}>
                  <Input
                    type="number"
                    step={0.01}
                    value={String(siteCost)}
                    contentAfter={props.currencyCode ? <span>{props.currencyCode}</span> : undefined}
                    onChange={(_, data) => setSiteCost(Number.parseFloat(data.value) || 0)}
                  />
                </Field>

                <Field label={t('versionConceptCell.status', 'Estado')}>
                  <RadioGroup
                    value={String(status)}
                    onChange={(_, data) => setStatus(Number(data.value))}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <Radio
                        key={opt.value}
                        value={String(opt.value)}
                        label={opt.label}
                      />
                    ))}
                  </RadioGroup>
                </Field>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 8,
                    borderTop: '1px solid var(--colorNeutralStroke1)',
                    paddingTop: 8,
                  }}
                >
                  <Button appearance="secondary" size="small" onClick={() => setIsOpen(false)}>
                    {t('versionConceptCell.cancel', 'Cancelar')}
                  </Button>
                  <Button appearance="primary" size="small" onClick={handleSave}>
                    {t('versionConceptCell.save', 'Guardar')}
                  </Button>
                </div>
              </div>
            </PopoverSurface>
          </Popover>
        </td>
      </>
    );
  }

  return (
    <>
      <td className={styles.tdVersion} style={{ padding: 0 }}>
        <Popover
          withArrow
          open={isOpen}
          onOpenChange={(_, data) => setIsOpen(!!data.open)}
        >
          <PopoverTrigger disableButtonEnhancement>
            <button
              className={styles.cellButton}
              title={t('versionConceptCell.editTooltip', 'Editar valores y estado')}
            >
              <span>{formatCurrency(vc.sponsorCost)}</span>
              <Edit16Regular style={{ opacity: 0.4, fontSize: 12, flexShrink: 0 }} />
            </button>
          </PopoverTrigger>
          <PopoverSurface
            tabIndex={-1}
            aria-label={t('versionConceptCell.popoverLabel', 'Editar concepto de versión')}
          >
            <div
              style={{
                minWidth: 270,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              <Text weight="semibold" size={300}>
                {t('versionConceptCell.title', 'Editar valores')}
              </Text>

              <Field label={t('versionConceptCell.sponsorCost', 'Oferta sponsor')}>
                <Input
                  type="number"
                  step={0.01}
                  value={String(sponsorCost)}
                  contentAfter={props.currencyCode ? <span>{props.currencyCode}</span> : undefined}
                  onChange={(_, data) => setSponsorCost(Number.parseFloat(data.value) || 0)}
                />
              </Field>

              <Field label={t('versionConceptCell.siteCost', 'Contraoferta')}>
                <Input
                  type="number"
                  step={0.01}
                  value={String(siteCost)}
                  contentAfter={props.currencyCode ? <span>{props.currencyCode}</span> : undefined}
                  onChange={(_, data) => setSiteCost(Number.parseFloat(data.value) || 0)}
                />
              </Field>

              <Field label={t('versionConceptCell.status', 'Estado')}>
                <RadioGroup
                  value={String(status)}
                  onChange={(_, data) => setStatus(Number(data.value))}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <Radio
                      key={opt.value}
                      value={String(opt.value)}
                      label={opt.label}
                    />
                  ))}
                </RadioGroup>
              </Field>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 8,
                  borderTop: '1px solid var(--colorNeutralStroke1)',
                  paddingTop: 8,
                }}
              >
                <Button
                  appearance="secondary"
                  size="small"
                  onClick={() => setIsOpen(false)}
                >
                  {t('versionConceptCell.cancel', 'Cancelar')}
                </Button>
                <Button
                  appearance="primary"
                  size="small"
                  disabled={props.isUpdating}
                  onClick={handleSave}
                >
                  {props.isUpdating && <Spinner size="extra-tiny" style={{ marginRight: 4 }} />}
                  {t('versionConceptCell.save', 'Guardar')}
                </Button>
              </div>
            </div>
          </PopoverSurface>
        </Popover>
      </td>

      <td className={styles.td} style={{ padding: 0 }}>
        <button
          className={styles.cellButton}
          onClick={() => setIsOpen(true)}
          title={t('versionConceptCell.editTooltip', 'Editar valores y estado')}
        >
          <span>{formatCurrency(vc.siteCost)}</span>
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              color: getStatusColor(vc.negotiationConceptStatus),
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            <StatusIcon status={vc.negotiationConceptStatus} />
            {getStatusLabel(vc.negotiationConceptStatus)}
          </span>
        </button>
      </td>
    </>
  );
};

export { VersionConceptCellComponent };
export default connect(mapStateToProps, mapDispatchToProps)(
  VersionConceptCellComponent as React.ComponentType<OwnProps>
);
